import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Paperclip, Trash2, Plus, Pencil, ExternalLink, Linkedin, Film } from "lucide-react";
import { BulkUpload, LinkedInPostDialog, VisitorsTab } from "./WebsiteExtras";
import SiteContentTab from "./SiteContentTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";

const isVideoPath = (p = "") => /\.(mp4|webm|mov)$/i.test(p);
const STATUSES = ["new", "in_progress", "responded", "closed", "spam"];
const STATUS_TONE = { new: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-800", responded: "bg-emerald-100 text-emerald-800", closed: "bg-slate-200 text-slate-700", spam: "bg-rose-100 text-rose-700" };

const FIELDS = {
  team: [["name", "Name"], ["group", "Section", "group"], ["title", "Title (optional, e.g. Partner)"], ["qualifications", "Qualifications"], ["practice", "Practice area"], ["experience", "Experience line (e.g. 18+ years · ex-Deloitte)"], ["email", "Email"], ["linkedin", "LinkedIn URL"], ["order", "Display order", "number"], ["photo", "Photo", "photo"], ["bio", "Bio", "textarea"]],
  jobs: [["title", "Role title"], ["category", "Category (Articleship / Qualified CA / …)"], ["location", "Location"], ["experience", "Experience"], ["type", "Type"], ["order", "Display order", "number"], ["description", "Description", "textarea"], ["requirements", "Requirements (one per line)", "lines"]],
  publications: [["title", "Title"], ["category", "Category (Direct Tax / GST & Indirect Tax / Audit Standards / Corporate Law / ESG & Risk)"], ["author", "Author"], ["published_on", "Published on", "date"], ["read_minutes", "Read minutes", "number"], ["external_url", "External link (optional)"], ["summary", "Summary", "textarea"], ["body", "Body", "textarea"]],
  quotes: [["quote", "Client quote *", "textarea"], ["name", "Client name (or leave blank)"], ["designation", "Designation (e.g. CFO)"], ["organisation", "Organisation / sector"], ["order", "Display order", "number"]],
  gallery: [["photo", "Photo *", "photo"], ["category", "Category", "gcat"], ["caption", "Caption (e.g. Diwali celebration 2025)"], ["taken_on", "Date / year (optional)"], ["order", "Display order", "number"]],
};
const LABEL = { team: "Team member", jobs: "Opening", publications: "Article", gallery: "Photo", quotes: "Client quote" };

function PhotoField({ value, onChange, folder = "team" }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { onChange((await api.post(`/website/team-photo?folder=${folder}`, fd)).data.path); toast.success("Photo uploaded"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3 mt-1">
      {value ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/media/${value}`} alt="" className="h-16 w-14 object-cover object-top rounded-sm border" data-testid="content-photo-preview" /> : <div className="h-16 w-14 bg-slate-100 rounded-sm border" />}
      <label className="text-sm text-blue-600 cursor-pointer hover:underline">
        {busy ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="content-photo-input" />
      </label>
      {value && <button type="button" className="text-xs text-rose-600" onClick={() => onChange("")} data-testid="content-photo-remove">Remove</button>}
    </div>
  );
}

async function downloadAttachment(e) {
  try {
    const r = await api.get(`/website/enquiries/${e.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = e.attachment.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (err) { toast.error(formatApiError(err)); }
}

function EnquiryRow({ e, onChange, onDelete, canDelete }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(e.notes || "");
  const patch = async (body) => {
    try { onChange((await api.patch(`/website/enquiries/${e.id}`, body)).data); toast.success("Updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`enq-row-${e.id}`}>
        <td className="p-3">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
        <td className="p-3 whitespace-nowrap text-slate-500">{new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
        <td className="p-3"><div className="font-medium">{e.name}</div><div className="text-xs text-slate-500">{e.email} · {e.phone}</div></td>
        <td className="p-3">{e.organisation || "—"}</td>
        <td className="p-3"><Badge variant="outline" className="mr-1">{e.kind === "application" ? "Career" : "Enquiry"}</Badge>{e.position || e.service}</td>
        <td className="p-3">
          {e.attachment ? <Paperclip className="h-4 w-4 text-slate-500 inline mr-1" /> : ""}
          {e.alert_status && <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm ${e.alert_status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} title={e.alert_detail || ""} data-testid={`enq-alert-${e.id}`}>{e.alert_status === "sent" ? "emailed" : `email ${e.alert_status}`}</span>}
        </td>
        <td className="p-3" onClick={(ev) => ev.stopPropagation()}>
          <Select value={e.status} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger className={`h-8 w-36 text-xs border-0 ${STATUS_TONE[e.status]}`} data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50 border-b border-slate-200" data-testid={`enq-detail-${e.id}`}>
          <td />
          <td colSpan={6} className="p-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Message</div>
                <p className="whitespace-pre-line text-sm">{e.message}</p>
                {e.attachment && (
                  <Button size="sm" variant="outline" className="mt-4" onClick={() => downloadAttachment(e)} data-testid={`enq-download-${e.id}`}>
                    <Paperclip className="h-3.5 w-3.5 mr-1" /> {e.attachment.filename}
                  </Button>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Internal notes</div>
                <Textarea rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)} data-testid={`enq-notes-${e.id}`} />
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={() => patch({ notes })} data-testid={`enq-save-notes-${e.id}`}>Save notes</Button>
                  <a href={`mailto:${e.email}?subject=${encodeURIComponent("Re: your enquiry with VCAJ & Associates LLP")}`}><Button size="sm" variant="outline" data-testid={`enq-reply-${e.id}`}>Reply by email</Button></a>
                  {canDelete && <Button size="sm" variant="ghost" className="text-rose-600 ml-auto" onClick={() => onDelete(e.id)} data-testid={`enq-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function EnquiriesTab({ canDelete }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const load = useCallback(() => {
    const params = kind !== "all" ? { kind } : {};
    api.get("/website/enquiries", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [kind]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/website/enquiries/${id}`); setRows(rows.filter((r) => r.id !== id)); } catch (e) { toast.error(formatApiError(e)); }
  };
  const counts = STATUSES.reduce((m, s) => ({ ...m, [s]: rows.filter((r) => r.status === s).length }), {});
  const shown = status === "all" ? rows : rows.filter((r) => r.status === status);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-44" data-testid="enq-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All submissions</SelectItem><SelectItem value="enquiry">Client enquiries</SelectItem><SelectItem value="application">Career applications</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="enq-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2 text-xs ml-auto" data-testid="enq-counts">
          {STATUSES.map((s) => <span key={s} className={`px-2 py-1 rounded-sm ${STATUS_TONE[s]}`}>{s.replace("_", " ")}: {counts[s]}</span>)}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="enq-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="w-8" /><th className="p-3 text-left">Received</th><th className="p-3 text-left">From</th><th className="p-3 text-left">Organisation</th><th className="p-3 text-left">Service / Role</th><th className="p-3" /><th className="p-3 text-left">Status</th></tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="enq-empty">No submissions yet.</td></tr>}
            {shown.map((e) => <EnquiryRow key={e.id} e={e} canDelete={canDelete} onDelete={del} onChange={(n) => setRows(rows.map((r) => (r.id === n.id ? n : r)))} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContentDialog({ kind, item, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ published: true, ...(item || {}), requirements: (item?.requirements || []).join("\n") }));
  const save = async () => {
    const body = { ...f };
    if (kind === "jobs") body.requirements = String(f.requirements || "").split("\n").map((s) => s.trim()).filter(Boolean);
    else delete body.requirements;
    ["order", "read_minutes"].forEach((k) => { if (k in body) body[k] = Number(body[k]) || 0; });
    try {
      const r = item?.id ? await api.put(`/website/content/${kind}/${item.id}`, body) : await api.post(`/website/content/${kind}`, body);
      onSaved(r.data); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="content-dialog">
        <DialogHeader><DialogTitle>{item?.id ? "Edit" : "Add"} {LABEL[kind].toLowerCase()}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS[kind].map(([k, label, type]) => (
            <label key={k} className={type === "textarea" || type === "lines" ? "col-span-2" : ""}>
              <span className="text-xs text-slate-500">{label}</span>
              {type === "photo" && isVideoPath(f[k])
                ? <div className="mt-1 text-sm text-slate-500 flex items-center gap-2"><Film className="h-4 w-4" /> Video uploaded</div>
                : type === "photo"
                ? <PhotoField value={f[k]} folder={kind === "gallery" ? "gallery" : "team"} onChange={(v) => setF({ ...f, [k]: v })} />
                : type === "gcat"
                ? (
                  <Select value={f[k] || "Office"} onValueChange={(v) => setF({ ...f, [k]: v })}>
                    <SelectTrigger data-testid="content-field-gcat"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Office", "Celebrations", "Events"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                )
                : type === "group"
                ? (
                  <Select value={f[k] || "partner"} onValueChange={(v) => setF({ ...f, [k]: v })}>
                    <SelectTrigger data-testid="content-field-group"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="partner">Partners</SelectItem><SelectItem value="team">Our professionals</SelectItem></SelectContent>
                  </Select>
                )
                : type === "textarea" || type === "lines"
                ? <Textarea rows={type === "lines" ? 4 : 6} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`content-field-${k}`} />
                : <Input type={type || "text"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`content-field-${k}`} />}
            </label>
          ))}
          <label className="flex items-center gap-3 col-span-2">
            <Switch checked={!!f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="content-field-published" />
            <span className="text-sm">Show on website</span>
          </label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={kind === "gallery" && !f.photo} data-testid="content-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContentTab({ kind }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [liOpen, setLiOpen] = useState(false);
  useEffect(() => { api.get(`/website/content/${kind}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))); }, [kind]);
  const del = async (id) => {
    if (!window.confirm("Delete this item from the website?")) return;
    try { await api.delete(`/website/content/${kind}/${id}`); setRows(rows.filter((r) => r.id !== id)); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saved = (doc) => { setRows(rows.some((r) => r.id === doc.id) ? rows.map((r) => (r.id === doc.id ? doc : r)) : [...rows, doc]); setEditing(null); };
  const sub = (r) => (kind === "team" ? [(r.group || "partner") === "team" ? "Professional" : "Partner", r.title, r.practice].filter(Boolean).join(" · ") : kind === "jobs" ? `${r.category} · ${r.location}` : kind === "gallery" ? [isVideoPath(r.photo) ? "Video" : "Photo", r.category, r.taken_on].filter(Boolean).join(" · ") : kind === "quotes" ? [r.name, r.designation, r.organisation].filter(Boolean).join(" · ") : `${r.source === "linkedin" ? "LinkedIn post · " : ""}${r.category} · ${r.published_on}`);
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Changes appear on the public website immediately.</p>
        <div className="flex gap-2">
          {kind === "publications" && <Button variant="outline" onClick={() => setLiOpen(true)} data-testid="content-add-linkedin"><Linkedin className="h-4 w-4 mr-1 text-[#0A66C2]" /> Add LinkedIn post</Button>}
          <Button onClick={() => setEditing({})} data-testid={`content-add-${kind}`}><Plus className="h-4 w-4 mr-1" /> Add {LABEL[kind].toLowerCase()}</Button>
        </div>
      </div>
      {kind === "gallery" && <BulkUpload onAdded={(docs) => setRows((rs) => [...docs, ...rs])} />}
      {liOpen && <LinkedInPostDialog onClose={() => setLiOpen(false)} onSaved={(doc) => { setRows((rs) => [doc, ...rs]); setLiOpen(false); }} />}
      <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid={`content-list-${kind}`}>
        {rows.length === 0 && <div className="p-8 text-center text-slate-500">Nothing here yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="p-4 flex items-center gap-4" data-testid={`content-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate flex items-center gap-3">
                {kind === "gallery" && r.photo && (isVideoPath(r.photo)
                  ? <span className="h-10 w-14 rounded-sm border bg-slate-900 text-white flex items-center justify-center"><Film className="h-4 w-4" /></span>
                  : <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/media/${r.photo}`} alt="" className="h-10 w-14 object-cover rounded-sm border" />)}
                {r.source === "linkedin" && <Linkedin className="h-4 w-4 text-[#0A66C2] shrink-0" />}
                {kind === "quotes" ? `“${r.quote}”` : r.name || r.title || r.caption || (isVideoPath(r.photo) ? "Untitled video" : "Untitled photo")}
              </div>
              <div className="text-xs text-slate-500 truncate">{sub(r)}</div>
            </div>
            {!r.published && <Badge variant="outline">Hidden</Badge>}
            <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`content-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(r.id)} data-testid={`content-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {editing && <ContentDialog kind={kind} item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
}

function LeadsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/website/tool-leads").then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(formatApiError(e)); }); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm" data-testid="leads-table">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="p-3 text-left">Downloaded</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Name</th><th className="p-3 text-left">Organisation</th><th className="p-3 text-left">Tool</th></tr>
        </thead>
        <tbody>
          {rows?.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500" data-testid="leads-empty">No PDF downloads yet.</td></tr>}
          {(rows || []).map((l) => (
            <tr key={l.id} className="border-b border-slate-100" data-testid={`lead-row-${l.id}`}>
              <td className="p-3 whitespace-nowrap text-slate-500">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
              <td className="p-3"><a href={`mailto:${l.email}`} className="text-blue-600 hover:underline">{l.email}</a></td>
              <td className="p-3">{l.name || "—"}</td>
              <td className="p-3">{l.organisation || "—"}</td>
              <td className="p-3">{l.tool_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WebsiteAdmin({ initialTab = "enquiries" }) {
  const { user } = useAuth();
  const canEdit = ["partner", "admin"].includes(user?.role);
  const canView = canEdit || user?.role === "manager";
  return (
    <div className="space-y-6" data-testid="website-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Public website</div>
          <h1 className="font-serif text-4xl mt-2">Website & Enquiries</h1>
        </div>
        <a href="/" target="_blank" rel="noreferrer"><Button variant="outline" data-testid="website-open-btn"><ExternalLink className="h-4 w-4 mr-1" /> Open website</Button></a>
      </div>
      <Tabs defaultValue={canEdit ? initialTab : "enquiries"}>
        <TabsList>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Tool leads</TabsTrigger>
          {canEdit && <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>}
          {canEdit && <TabsTrigger value="jobs" data-testid="tab-jobs">Careers</TabsTrigger>}
          {canEdit && <TabsTrigger value="publications" data-testid="tab-publications">Let's Learn</TabsTrigger>}
          {canEdit && <TabsTrigger value="gallery" data-testid="tab-gallery">Life at VCAJ</TabsTrigger>}
          {canEdit && <TabsTrigger value="quotes" data-testid="tab-quotes">Client quotes</TabsTrigger>}
          {canView && <TabsTrigger value="visitors" data-testid="tab-visitors">Visitors</TabsTrigger>}
          {canEdit && <TabsTrigger value="site-content" data-testid="tab-site-content">Site content</TabsTrigger>}
        </TabsList>
        <TabsContent value="enquiries" className="mt-6"><EnquiriesTab canDelete={canEdit} /></TabsContent>
        <TabsContent value="leads" className="mt-6"><LeadsTab /></TabsContent>
        <TabsContent value="visitors" className="mt-6"><VisitorsTab /></TabsContent>
        {canEdit && <TabsContent value="site-content" className="mt-6"><SiteContentTab /></TabsContent>}
        {canEdit && ["team", "jobs", "publications", "gallery", "quotes"].map((k) => <TabsContent key={k} value={k} className="mt-6"><ContentTab kind={k} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
